import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import * as Sentry from '@sentry/browser';

import LoggedInUserContext from '../../../context/LoggedInUserContext';
import InternalAssignment from '../../../Model/InternalAssignment';
import { AssignmentRole } from '../../../utils/internalAssignments';
import CoachTask from '../../Model/CoachTask';
import { activityStatus } from '../../Model/CoachActivity';
import { groupCamAssignments, coachIdsForSet } from '../../pages/CoachTaskAdmin/utils';
import CoachesListContext from '../CoachesListContext';
import CoachTaskAdminContext, { initialValues } from './CoachTaskAdminContext';

const emptyCounts = () => ({ open: 0, completed: 0, overdue: 0 });

/**
 * Loads the coach-task admin data once and exposes it to both the overview (counts) and
 * the per-coach detail (task list), so neither page re-resolves assignments or re-fetches
 * tasks. Task data is held in live coachActivity collections and derived reactively (via
 * autorun), so a completion/removal — here or on web — updates counts and lists with no
 * manual refresh. Names come from coach docs (CoachesListContext).
 */
const CoachTaskAdminContextProvider = ({ children }) => {
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { coachesList, isReady: coachesReady } = useContext(CoachesListContext);

  const [managedCoachIds, setManagedCoachIds] = useState(initialValues.managedCoachIds);
  const [isAssignmentsReady, setIsAssignmentsReady] = useState(false);
  const [coachSet, setCoachSet] = useState(initialValues.coachSet);
  const [{ camOptions, coachIdsByCam }, setCamAssignments] = useState({
    camOptions: initialValues.camOptions,
    coachIdsByCam: {},
  });
  const [taskCollections, setTaskCollections] = useState([]);
  const [tasksByCoach, setTasksByCoach] = useState(initialValues.tasksByCoach);
  const [countsByCoach, setCountsByCoach] = useState(initialValues.countsByCoach);

  // Resolve the logged-in AC/CAM's managed coaches once.
  useEffect(() => {
    if (!loggedInUserId) {
      return undefined;
    }
    let isActive = true;
    const loadManagedCoaches = async () => {
      try {
        const [acAssignments, camAssignments] = await Promise.all([
          InternalAssignment.getAllCurrentAssignments(loggedInUserId, AssignmentRole.ASSISTANT_COACH),
          InternalAssignment.getAllCurrentAssignments(loggedInUserId, AssignmentRole.CAM),
        ]);
        if (!isActive) {
          return;
        }
        setManagedCoachIds([...new Set([
          ...acAssignments.docs.map((assignment) => assignment.coach),
          ...camAssignments.docs.map((assignment) => assignment.coach),
        ])].filter(Boolean));
      } catch (error) {
        Sentry.captureException(error, { extra: { loggedInUserId } });
      } finally {
        if (isActive) {
          setIsAssignmentsReady(true);
        }
      }
    };
    loadManagedCoaches();
    return () => {
      isActive = false;
    };
  }, [loggedInUserId]);

  // Every CAM's current coaches, once, for the coach-set picker.
  useEffect(() => {
    let isActive = true;
    const loadCamAssignments = async () => {
      try {
        const assignments = await InternalAssignment.getAllCurrentAssignmentsByRole(AssignmentRole.CAM);
        if (isActive) {
          setCamAssignments(groupCamAssignments(
            assignments.docs.map(({ userId, userName, coach }) => ({ userId, userName, coach })),
          ));
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { loggedInUserId } });
      }
    };
    loadCamAssignments();
    return () => {
      isActive = false;
    };
  }, [loggedInUserId]);

  // Active set: the managed coaches, every coach, or one CAM's coaches.
  const activeCoachIds = useMemo(
    () => coachIdsForSet(coachSet, {
      managedCoachIds,
      allCoachIds: Object.keys(coachesList),
      coachIdsByCam,
    }),
    [coachSet, coachesList, managedCoachIds, coachIdsByCam],
  );

  // Build the live task collections for the active set (rebuilt when the set changes).
  useEffect(() => {
    let isActive = true;
    if (!activeCoachIds.length) {
      setTaskCollections([]);
      return undefined;
    }
    const loadTaskCollections = async () => {
      try {
        const collections = await CoachTask.getTasksCollectionsForCoaches(activeCoachIds);
        if (isActive) {
          setTaskCollections(collections);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { loggedInUserId, coachSet } });
      }
    };
    loadTaskCollections();
    return () => {
      isActive = false;
    };
  }, [activeCoachIds, loggedInUserId, coachSet]);

  // Derive per-coach task lists + counts reactively from the live collections.
  useEffect(() => {
    const disposer = autorun(() => {
      const nextTasks = {};
      const nextCounts = {};
      activeCoachIds.forEach((coachId) => {
        nextTasks[coachId] = [];
        nextCounts[coachId] = emptyCounts();
      });
      taskCollections.forEach((collection) => {
        if (!collection.hasDocs) {
          return;
        }
        collection.docs.forEach((task) => {
          // A removal is the end of a task, except a client's no to a highlight, which the AC who made it
          // needs to see. It is listed, but counts towards nothing.
          if (task.status === activityStatus.REMOVED && !task.isDeclinedByClient) {
            return;
          }
          const { coach: coachId } = task;
          if (!nextTasks[coachId]) {
            nextTasks[coachId] = [];
            nextCounts[coachId] = emptyCounts();
          }
          nextTasks[coachId].push(task);
          if (task.status === activityStatus.IN_PROGRESS) {
            nextCounts[coachId].open += 1;
          } else if (task.status === activityStatus.COMPLETED) {
            nextCounts[coachId].completed += 1;
          }
          if (task.isOverdue) {
            nextCounts[coachId].overdue += 1;
          }
        });
      });
      setTasksByCoach(nextTasks);
      setCountsByCoach(nextCounts);
    });
    return disposer;
  }, [taskCollections, activeCoachIds]);

  const isReady = coachesReady && isAssignmentsReady;

  const value = useMemo(() => ({
    isReady,
    coachSet,
    setCoachSet,
    camOptions,
    managedCoachIds,
    activeCoachIds,
    coachNameById: coachesList,
    tasksByCoach,
    countsByCoach,
  }), [isReady, coachSet, camOptions, managedCoachIds, activeCoachIds, coachesList, tasksByCoach, countsByCoach]);

  return (
    <CoachTaskAdminContext.Provider value={value}>
      {children}
    </CoachTaskAdminContext.Provider>
  );
};

CoachTaskAdminContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(CoachTaskAdminContextProvider);
